import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs, collectionGroup, documentId, orderBy, limit, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import { Spinner } from '../../components/Spinner/Spinner';
import { ShowCard } from '../../components/ShowCard';
import UserAvatar from '../../components/UserAvatar/UserAvatar';
import { getShowRatings, getFriendRatings, getWatchlistShows, getSeenShows, getUserProfile, getUserLastActivity, addToWatchlist, markShowAsWatching, markShowAsSeen, archiveShow, getUserLists, addRating, updateLastViewedTimestamp, addActivity, getLatestActivities } from '../../services/databaseService';
import { StreamingSourceModal } from '../../components/StreamingSourceModal/StreamingSourceModal';
import { ShowCardSkeleton } from '../../components/ShowCardSkeleton';
import './FriendProfile.css';
import { pageStateStore } from '../../services/pageStateStore';
import { Link } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { RatingOnlyModal } from '../../components/RatingOnlyModal';
import { Toast } from '../../components/Toast/Toast';
import LoginPrompt from '../../components/LoginPrompt/LoginPrompt';
import RatingsOverviewModal from '../../components/RatingsOverviewModal/RatingsOverviewModal';

// Constants at the top of the file
const CACHE_KEY_PREFIX = 'friendProfile_';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const BATCH_SIZE = 30; // For batched show details fetching
const SHOW_STATUSES = {
  NONE: 'none',
  WATCHING: 'watching',
  WATCHLIST: 'watchlist',
  SEEN: 'seen',
  ARCHIVED: 'archived'
};

// Add this component at the top of the file, before FriendProfile
function FriendProfileSkeleton() {
  return (
    <div className="friend-profile-container">
      <div className="friend-header">
        <div className="avatar-skeleton" style={{ width: '50px', height: '50px' }}></div>
        <div className="friend-info">
          <div className="name-skeleton" style={{ width: '250px', height: '28px' }}></div>
          <div className="last-active-skeleton" style={{ width: '180px', height: '16px' }}></div>
        </div>
      </div>

      <div className="tab-container">
        {['Aktivitet', 'Topplista', 'Tittar på', 'Ska se', 'Har sett'].map((_, i) => (
          <div 
            key={i} 
            className="tab-button-skeleton"
            style={{ 
              width: '90px',
              height: '36px',
              borderRadius: '18px',
              margin: '0 8px'
            }}
          ></div>
        ))}
      </div>

      <div className="shows-list">
        {Array(8).fill(0).map((_, i) => (
          <ShowCardSkeleton key={i} />
        ))}
      </div>
    </div>
  );
}

// Add this component before the FriendProfile component
const StatusMenu = ({ show, position, onClose, currentStatus, onStatusChange }) => {
  return createPortal(
    <>
      <div className="popular-status-menu-overlay" onClick={onClose} />
      <div 
        className="popular-status-menu" 
        style={{ 
          position: 'fixed',
          left: `${position.left}px`,
          top: `${position.top}px`
        }}
      >
        <button 
          className={`status-option ${currentStatus === SHOW_STATUSES.WATCHING ? 'active' : ''}`}
          onClick={() => onStatusChange(show, SHOW_STATUSES.WATCHING)}
        >
          <i className="fas fa-satellite-dish"></i>
          <span>Tittar på</span>
        </button>
        <button 
          className={`status-option ${currentStatus === SHOW_STATUSES.WATCHLIST ? 'active' : ''}`}
          onClick={() => onStatusChange(show, SHOW_STATUSES.WATCHLIST)}
        >
          <i className="fas fa-check"></i>
          <span>Ska se</span>
        </button>
        <button 
          className={`status-option ${currentStatus === SHOW_STATUSES.SEEN ? 'active' : ''}`}
          onClick={() => onStatusChange(show, SHOW_STATUSES.SEEN)}
        >
          <i className="fas fa-check-double"></i>
          <span>Har sett</span>
        </button>
        <button 
          className={`status-option ${currentStatus === SHOW_STATUSES.ARCHIVED ? 'active' : ''}`}
          onClick={() => onStatusChange(show, SHOW_STATUSES.ARCHIVED)}
        >
          <i className="fas fa-trash"></i>
          <span>Arkivera</span>
        </button>
      </div>
    </>,
    document.body
  );
};

const CustomSelect = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const options = [
    { value: 'all', label: 'Alla' },
    { value: 'movie', label: 'Filmer' },
    { value: 'series', label: 'Serier' }
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <>
      <div className="friend-profile__filter-container">
        <button 
          className="friend-profile__filter-button"
          onClick={() => setIsOpen(!isOpen)}
        >
          {options.find(opt => opt.value === value)?.label}
        </button>
      </div>

      {isOpen && createPortal(
        <>
          <div className="friend-profile__filter-overlay" onClick={() => setIsOpen(false)} />
          <div className="friend-profile__filter-drawer" ref={dropdownRef}>
            <div className="friend-profile__filter-drawer-header">
              <div className="friend-profile__filter-drawer-handle" />
              <h3>Filtrera</h3>
            </div>
            <div className="friend-profile__filter-drawer-content">
              {options.map((option) => (
                <button
                  key={option.value}
                  className={`friend-profile__filter-option ${value === option.value ? 'active' : ''}`}
                  onClick={() => {
                    onChange(option.value);
                    setIsOpen(false);
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
};

// Add this helper function before the FriendProfile component
const hasShowsForCurrentTab = (activeTab, ratedShows, watchingShows, watchlistShows, seenShows) => {
  switch (activeTab) {
    case 'rated':
      return ratedShows.length > 0;
    case 'watching':
      return watchingShows.length > 0;
    case 'watchlist':
      return watchlistShows.length > 0;
    case 'seen':
      return seenShows.length > 0;
    case 'activity':
      return true; // Don't show filter for activity tab
    default:
      return false;
  }
};

// Add this helper function
const getNoRatingText = (profile, isOwnProfile) => {
  if (isOwnProfile) {
    return "Du har inte gett betyg";
  }
  // Get first letter of the first name, fallback to first letter of display name
  const firstLetter = profile.firstName 
    ? profile.firstName[0]
    : profile.displayName?.[0] || '?';
  return `${firstLetter} har inte gett betyg`;
};

function FriendProfile() {
  const { userId } = useParams();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [initialLoad, setInitialLoad] = useState(true);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [selectedShowForStatus, setSelectedShowForStatus] = useState(null);
  const [currentShowStatus, setCurrentShowStatus] = useState('none');
  const [menuPosition, setMenuPosition] = useState({ left: 0, top: 0 });
  const [toast, setToast] = useState(null);
  const [loadingRatings, setLoadingRatings] = useState(false);
  const [showRatingsOverview, setShowRatingsOverview] = useState(false);
  const [selectedShowForRating, setSelectedShowForRating] = useState(null);
  const [showRatingOnlyModal, setShowRatingOnlyModal] = useState(false);
  const [userLists, setUserLists] = useState({ watchlist: [], seenList: [], watchingList: [], archivedList: [] });
  const [filter, setFilter] = useState(() => {
    return pageStateStore.getState('friendProfile')?.filter || 'all';
  });
  const [showRatings, setShowRatings] = useState([]);
  const navigate = useNavigate();
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);

  // Get initial tab based on conditions
  const getInitialTab = () => {
    // Check if we have unseen activities from Popular page
    const hasUnseenActivity = location.state?.hasUnseenActivity;
    if (hasUnseenActivity) {
      return 'activity';
    }

    // If no unseen activities, try to get the saved tab from pageStateStore
    const savedState = pageStateStore.getState('friendProfile');
    if (savedState?.userId === userId && savedState?.tab) {
      return savedState.tab;
    }

    // Default to URL param or 'rated'
    return searchParams.get('tab') || 'rated';
  };

  // Set initial URL params if needed
  useEffect(() => {
    const currentTab = searchParams.get('tab');
    const initialTab = getInitialTab();
    
    if (!currentTab || currentTab !== initialTab) {
      setSearchParams({ tab: initialTab });
    }
  }, []);

  // Use URL parameters for tab state
  const activeTab = searchParams.get('tab') || 'rated';

  // Update tab handler to also save to pageStateStore
  const handleTabChange = useCallback((newTab) => {
    setSearchParams({ tab: newTab });
    // Save the current tab in pageStateStore
    pageStateStore.setState('friendProfile', {
      ...pageStateStore.getState('friendProfile'),
      userId,
      tab: newTab
    });
  }, [setSearchParams, userId]);

  // Add effect to fetch activities
  useEffect(() => {
    const fetchActivities = async () => {
      if (activeTab !== 'activity') return;
      
      setLoadingActivities(true);
      try {
        const allActivities = await getLatestActivities();
        // Filter activities for this specific user
        const userActivities = allActivities[userId] || [];
        setActivities(userActivities.sort((a, b) => b.timestamp?.seconds - a.timestamp?.seconds));
      } catch (error) {
        console.error('Error fetching activities:', error);
      } finally {
        setLoadingActivities(false);
      }
    };

    fetchActivities();
  }, [userId, activeTab]);

  // Get cached state from pageStateStore first
  const getInitialState = () => {
    const pageState = pageStateStore.getState('friendProfile');
    if (pageState?.userId === userId) {
      return pageState;
    }
    
    // Fall back to session storage if no pageState
    const cachedData = sessionStorage.getItem(`${CACHE_KEY_PREFIX}${userId}`);
    if (cachedData) {
      const { timestamp, data } = JSON.parse(cachedData);
      if (Date.now() - timestamp < CACHE_DURATION) {
        return data;
      }
    }
    return null;
  };

  const initialState = useMemo(() => getInitialState(), [userId]);

  // Initialize states with initial state
  const [friendProfile, setFriendProfile] = useState(initialState?.profile || null);
  const [ratedShows, setRatedShows] = useState(initialState?.ratedShows || []);
  const [watchlistShows, setWatchlistShows] = useState(initialState?.watchlistShows || []);
  const [watchingShows, setWatchingShows] = useState(initialState?.watchingShows || []);
  const [seenShows, setSeenShows] = useState(initialState?.seenShows || []);
  const [lastActive, setLastActive] = useState(initialState?.lastActive || null);
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(!initialState);
  const [showStreamingModal, setShowStreamingModal] = useState(false);
  const [selectedShowForSources, setSelectedShowForSources] = useState(null);
  const [error, setError] = useState(null);
  const [tabContentLoading, setTabContentLoading] = useState(!initialState);

  // Add this check to determine if the current user is viewing their own profile
  const isOwnProfile = useMemo(() => user?.uid === userId, [user?.uid, userId]);

  // Save state to pageStateStore when component updates
  useEffect(() => {
    if (friendProfile) {
      pageStateStore.setState('friendProfile', {
        userId,
        profile: friendProfile,
        ratedShows,
        watchlistShows,
        watchingShows,
        seenShows,
        lastActive
      });
    }
  }, [userId, friendProfile, ratedShows, watchlistShows, watchingShows, seenShows, lastActive]);

  // Add cache check for all tabs
  const getCachedData = () => {
    const cachedKey = `${CACHE_KEY_PREFIX}${userId}`;
    const cached = sessionStorage.getItem(cachedKey);
    if (cached) {
      const { timestamp, data } = JSON.parse(cached);
      if (Date.now() - timestamp < CACHE_DURATION) {
        return data;
      }
    }
    return null;
  };

  // Use cached data for initial state
  const cachedData = useMemo(() => getCachedData(), [userId]);

  // Add this function to handle caching
  const cacheProfileData = useCallback((data) => {
    const cacheData = {
      timestamp: Date.now(),
      data: {
        profile: data.profile,
        ratedShows: data.ratedShows,
        watchlistShows: data.watchlistShows,
        watchingShows: data.watchingShows,
        seenShows: data.seenShows,
        lastActive: data.lastActive
      }
    };
    sessionStorage.setItem(`${CACHE_KEY_PREFIX}${userId}`, JSON.stringify(cacheData));
  }, [userId]);

  // 1. Define fetchShowDetails first
  const fetchShowDetails = useCallback(async (showIds) => {
    if (!showIds?.length) return {};
    
    try {
      const showDetails = {};
      const uniqueIds = [...new Set(showIds)];
      const showsRef = collection(db, 'shows');

      const batchPromises = Array.from({ length: Math.ceil(uniqueIds.length / BATCH_SIZE) })
        .map((_, index) => {
          const batch = uniqueIds.slice(index * BATCH_SIZE, (index + 1) * BATCH_SIZE);
          return getDocs(query(showsRef, where(documentId(), 'in', batch)));
        });

      const snapshots = await Promise.all(batchPromises);
      
      snapshots.forEach(snapshot => {
        snapshot.docs.forEach(doc => {
          const data = doc.data();
          showDetails[doc.id] = {
            ...data,
            posterPath: data.posterPath && data.posterPath !== "N/A" 
              ? data.posterPath 
              : '/images/default-poster.png'
          };
        });
      });

      return showDetails;
    } catch (error) {
      console.error('Error fetching show details:', error);
      return {};
    }
  }, []);

  // 2. Then define fetchUserData which depends on fetchShowDetails
  const fetchUserData = useCallback(async () => {
    if (initialState) {
      console.log('Using cached state, skipping fetch');
      return;
    }

    try {
      setLoading(true);
      // First, quickly fetch and display critical user data
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (!userDoc.exists()) {
        setFriendProfile(null);
        setLoading(false);
        return;
      }

      const userData = userDoc.data();
      setFriendProfile(userData);
      setLoading(false); // Allow UI to render with basic data
      
      // Then fetch lists in order of importance
      const fetchLists = async () => {
        setTabContentLoading(true);
        
        // Start with rated shows (most important)
        const ratingsSnap = await getDocs(
          query(collectionGroup(db, 'ratings'), 
          where('userId', '==', userId),
          orderBy('timestamp', 'desc')
        ));

        const ratedData = ratingsSnap.docs.map(doc => ({
          id: doc.ref.path.split('/')[1],
          ...doc.data(),
          addedAt: doc.data().timestamp || doc.data().updatedAt || doc.data().createdAt
        }));

        // Get show details for rated shows first
        const ratedShowIds = ratedData.map(item => item.id);
        const initialShowDetails = await fetchShowDetails(ratedShowIds);
        
        // Update rated shows immediately
        setRatedShows(ratedData.map(item => ({
          ...item,
          ...initialShowDetails[item.id]
        })).sort((a, b) => b.addedAt - a.addedAt));

        // Then fetch other lists in parallel
        const [watchlistSnap, watchingSnap, seenSnap] = await Promise.all([
          getDocs(collection(db, `users/${userId}/watchlist`)),
          getDocs(collection(db, `users/${userId}/watching`)),
          getDocs(collection(db, `users/${userId}/seen`))
        ]);

        // Process other lists data
        const watchlistData = watchlistSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          addedAt: doc.data().addedAt
        }));

        const watchingData = watchingSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          addedAt: doc.data().addedAt
        }));

        const seenData = seenSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          addedAt: doc.data().addedAt
        }));

        // Get show details for other lists
        const otherShowIds = [
          ...watchlistData.map(item => item.id),
          ...watchingData.map(item => item.id),
          ...seenData.map(item => item.id)
        ];

        const otherShowDetails = await fetchShowDetails(otherShowIds);

        // Update other lists states
        setWatchlistShows(watchlistData.map(item => ({
          ...item,
          ...otherShowDetails[item.id]
        })).sort((a, b) => b.addedAt - a.addedAt));

        setWatchingShows(watchingData.map(item => ({
          ...item,
          ...otherShowDetails[item.id]
        })).sort((a, b) => b.addedAt - a.addedAt));

        setSeenShows(seenData.map(item => ({
          ...item,
          ...otherShowDetails[item.id]
        })).sort((a, b) => b.addedAt - a.addedAt));

        setTabContentLoading(false);
      };

      // Start the progressive loading
      fetchLists();
      
    } catch (error) {
      console.error('Error in fetchUserData:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [userId, fetchShowDetails, initialState]);

  useEffect(() => {
    if (userId) {
      console.log('[Debug] useEffect triggered with userId:', userId);
      fetchUserData();
    }
  }, [userId]);

  // Add debug logging for render
  console.log('[Debug] Render state:', {
    loading,
    ratedShowsCount: ratedShows.length,
    activeTab,
    userId
  });

  // Simplify the tab change effect since we now load all data upfront
  useEffect(() => {
    setTabContentLoading(false);
  }, [activeTab]);

  const fetchRatedShows = async () => {
    try {
      // Get all ratings across all shows for this user using collectionGroup
      const ratingsQuery = query(
        collectionGroup(db, 'ratings'),
        where('userId', '==', userId)
      );
      
      const ratingsSnapshot = await getDocs(ratingsQuery);
      console.log(`Found ${ratingsSnapshot.size} ratings for user ${userId}`);
      
      // Get all show details in parallel
      const showPromises = ratingsSnapshot.docs.map(async (ratingDoc) => {
        // Get showId from the rating document's path (shows/{showId}/ratings/{userId})
        const showId = ratingDoc.ref.parent.parent.id;
        const showDoc = await getDoc(doc(db, 'shows', showId));
        
        if (!showDoc.exists()) return null;
        
        const ratingData = ratingDoc.data();
        return {
          id: showId,
          ...showDoc.data(),
          rating: Math.round(ratingData.rating), // Personal rating
          userRating: Math.round(ratingData.rating), // Personal rating
          ratedAt: ratingData.timestamp?.toDate?.() || new Date(),
          averageRating: showDoc.data().averageRating || 0,
          ratingCount: showDoc.data().ratingCount || 0
        };
      });

      const shows = (await Promise.all(showPromises))
        .filter(show => show !== null)
        .sort((a, b) => {
          // First sort by rating (highest first)
          if (b.userRating !== a.userRating) {
            return b.userRating - a.userRating;
          }
          // If ratings are equal, sort by date (newest first)
          return b.ratedAt - a.ratedAt;
        });
      
      console.log('Final rated shows array:', shows);
      return shows;
    } catch (error) {
      console.error('Error fetching rated shows:', error);
      return [];
    }
  };

  const fetchSavedShows = async () => {
    const savedRef = collection(db, `users/${userId}/watchlist`);
    const snapshot = await getDocs(savedRef);
    const savedItems = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Fetch complete show details
    const showIds = savedItems.map(item => item.id);
    const showDetails = await fetchShowDetails(showIds);

    // Fetch ratings for all shows
    const ratingsPromises = showIds.map(id => getShowRatings(id));
    const ratingsResults = await Promise.all(ratingsPromises);
    
    const ratingsMap = {};
    ratingsResults.forEach((result, index) => {
      const showId = showIds[index];
      ratingsMap[showId] = {
        averageRating: result.averageRating,
        ratingCount: result.ratingCount
      };
    });

    // Combine the data
    return savedItems.map(item => ({
      ...item,
      ...showDetails[item.id],
      averageRating: ratingsMap[item.id]?.averageRating || 0,
      ratingCount: ratingsMap[item.id]?.ratingCount || 0,
      addedAt: item.addedAt
    }));
  };

  const fetchWatchingShows = async () => {
    const watchingRef = collection(db, `users/${userId}/watching`);
    const snapshot = await getDocs(watchingRef);
    const watchingItems = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Fetch complete show details
    const showIds = watchingItems.map(item => item.id);
    const showDetails = await fetchShowDetails(showIds);

    // Fetch individual ratings for each show
    const ratingsPromises = showIds.map(id => 
      getUserRating(userId, id)
    );
    const ratings = await Promise.all(ratingsPromises);
    
    // Create a map of ratings
    const ratingsMap = {};
    ratings.forEach((rating, index) => {
      if (rating) {
        ratingsMap[showIds[index]] = rating.rating;
      }
    });

    // Combine the data
    return watchingItems.map(item => ({
      ...item,
      ...showDetails[item.id],
      rating: ratingsMap[item.id], // Add individual rating
      addedAt: item.addedAt
    }));
  };

  const fetchWatchlistShows = async () => {
    try {
      // Get basic watchlist items
      const watchlistItems = await getWatchlistShows(userId);
      
      // Get show IDs
      const showIds = watchlistItems.map(item => item.id);
      
      // Fetch complete show details
      const showDetails = await fetchShowDetails(showIds);

      // Combine watchlist items with show details
      return watchlistItems.map(item => ({
        ...item,
        ...showDetails[item.id],
        addedAt: item.addedAt
      }));
    } catch (error) {
      console.error('Error fetching watchlist shows:', error);
      return [];
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const sortRatedShows = (shows) => {
    return [...shows].sort((a, b) => b.rating - a.rating);
  };

  const getActiveShows = () => {
    let shows = [];
    switch (activeTab) {
      case 'rated':
        // For rated shows, keep the user's personal rating
        shows = sortRatedShows(ratedShows);
        break;
      case 'watching':
      case 'watchlist':
      case 'seen':
        // For other tabs, ensure we're using the average rating
        shows = (activeTab === 'watching' ? watchingShows :
                activeTab === 'watchlist' ? watchlistShows :
                seenShows).map(show => ({
          ...show,
          rating: show.averageRating, // Override the personal rating with average rating
          userRating: null // Clear the user rating to ensure it doesn't display
        }));
        break;
      default:
        shows = [];
    }

    // Apply filter
    if (filter !== 'all') {
      return shows.filter(show => {
        if (filter === 'movie') {
          return show.type?.toLowerCase() === 'movie' || show.type?.toLowerCase() === 'film';
        } else if (filter === 'series') {
          return show.type?.toLowerCase() === 'series' || show.type?.toLowerCase() === 'tv_series';
        }
        return true;
      });
    }

    return shows;
  };

  const handleStreamingSourceClick = (show) => {
    setSelectedShowForSources(show);
    setShowStreamingModal(true);
  };

  const handleShowClick = (show) => {
    navigate(`/show/${show.id}`);
  };

  const handleActivityShowClick = (showId) => {
    navigate(`/show/${showId}`);
  };

  const renderShows = () => {
    const shows = getActiveShows();
    
    if (tabContentLoading) {
      return Array(6).fill(0).map((_, i) => (
        <ShowCardSkeleton key={i} />
      ));
    }

    if (!shows || shows.length === 0) {
      return (
        <div className="no-shows-message">
          Inga {
            activeTab === 'rated' ? 'topplista' : 
            activeTab === 'watchlist' ? 'sparade' : 
            activeTab === 'watching' ? 'pågående' :
            activeTab === 'seen' ? 'sedda' : 
            ''
          } titlar
        </div>
      );
    }

    return shows.map(show => (
      <ShowCard
        key={show.id}
        show={{
          ...show,
          posterPath: show.posterPath && show.posterPath !== "N/A" 
            ? show.posterPath 
            : '/images/default-poster.png',
          noRatingText: getNoRatingText(friendProfile, isOwnProfile),
          rating: activeTab === 'rated' ? show.rating : undefined,
          averageRating: activeTab !== 'rated' ? show.averageRating : undefined,
          ratingCount: activeTab !== 'rated' ? show.ratingCount : undefined
        }}
        activeTab={activeTab}
        hideIndex={true}
        showStatus={activeTab === 'rated' ? getShowStatus(show, userLists) : activeTab}
        readonly={!isOwnProfile}
        hideActions={!isOwnProfile}
        onStreamingSourceClick={() => handleStreamingSourceClick(show)}
        onStatusClick={isOwnProfile ? (e) => handleStatusClick(show, e) : undefined}
        onRatingClick={isOwnProfile ? (e) => handleRatingClick(e, show) : undefined}
        onWatchlistClick={isOwnProfile ? (e) => handleWatchlistClick(e, show) : undefined}
        onShareClick={isOwnProfile ? (e) => handleShare(e, show) : undefined}
        onRatingOverviewClick={(e) => handleRatingOverviewClick(e, show)}
        onMarkAsSeen={isOwnProfile ? (e) => handleMarkAsSeen(e, show) : undefined}
        onClick={() => handleShowClick(show)}
      />
    ));
  };

  // Add this function to invalidate cache
  const invalidateCache = useCallback(() => {
    sessionStorage.removeItem(`${CACHE_KEY_PREFIX}${userId}`);
  }, [userId]);

  // Add this effect to handle cache invalidation on tab changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        invalidateCache();
        fetchUserData();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [invalidateCache, fetchUserData]);

  // Add cleanup for cache when component unmounts
  useEffect(() => {
    return () => {
      // Optionally clear old caches here if needed
      const now = Date.now();
      Object.keys(sessionStorage).forEach(key => {
        if (key.startsWith(CACHE_KEY_PREFIX)) {
          try {
            const { timestamp } = JSON.parse(sessionStorage.getItem(key));
            if (now - timestamp > CACHE_DURATION) {
              sessionStorage.removeItem(key);
            }
          } catch (e) {
            sessionStorage.removeItem(key);
          }
        }
      });
    };
  }, []);

  // Clean up pageState when component unmounts
  useEffect(() => {
    return () => {
      // Only clean up if navigating to a different user
      const currentPath = window.location.pathname;
      if (!currentPath.includes(userId)) {
        pageStateStore.setState('friendProfile', null);
      }
    };
  }, [userId]);

  // Move formatActivityText inside the component and after handleShowClick
  const formatActivityText = (activity, profile) => {
    const userName = profile?.displayName?.split(' ')[0] || 'Någon';
    const showTitle = (
      <span 
        className="activity-show-title"
        onClick={(e) => {
          e.stopPropagation();
          handleActivityShowClick(activity.showId);
        }}
      >
        {activity.showTitle}
      </span>
    );
    
    switch (activity.action) {
      case 'rating':
        return <>{userName} gav {showTitle} {activity.value} smultron</>;
      
      case 'status':
        const statusText = {
          'watching': 'Tittar på',
          'watchlist': 'Ska se',
          'seen': 'Har sett',
          'archived': 'Arkiverade'
        }[activity.value] || activity.value;
        return <>{userName} markerade {showTitle} som "{statusText}"</>;
      
      case 'streaming':
        return <>{userName} la till {activity.value} som streamingtjänst för {showTitle}</>;
      
      case 'update':
        if (activity.changes?.status) {
          const newStatus = {
            'watching': 'Tittar på',
            'watchlist': 'Ska se',
            'seen': 'Har sett',
            'archived': 'Arkiverade'
          }[activity.changes.status] || activity.changes.status;
          return <>{userName} ändrade status på {showTitle} till "{newStatus}"</>;
        }
        if (activity.changes?.rating) {
          return <>{userName} ändrade betyg på {showTitle} till {activity.changes.rating} smultron</>;
        }
        return <>{userName} uppdaterade {showTitle}</>;

      default:
        return <>{userName} interagerade med {showTitle}</>;
    }
  };

  // Restore state when component mounts
  useEffect(() => {
    const savedState = pageStateStore.getState('friendProfile');
    if (savedState && savedState.userId === userId) {
      handleTabChange(savedState.tab || 'rated');
      // Restore scroll position after render
      setTimeout(() => {
        window.scrollTo(0, savedState.scrollPosition || 0);
      }, 100);
    }
  }, [userId, handleTabChange]);

  const handleProtectedAction = () => {
    if (!user) {
      setShowLoginPrompt(true);
      return false;
    }
    return true;
  };

  const handleStatusClick = (show, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!handleProtectedAction()) return;
    
    const buttonRect = e.currentTarget.getBoundingClientRect();
    
    // Position menu to the left of the button by default
    let left = buttonRect.left - 220; // menu width (200px) + gap (20px)
    let top = buttonRect.top;
    
    // If not enough space on the left, position to the right
    if (left < 16) {
      left = buttonRect.right + 20;
    }
    
    // If not enough space on either side, position below
    if (left + 200 > window.innerWidth - 16) {
      left = buttonRect.left;
      top = buttonRect.bottom + 8;
    }
    
    setMenuPosition({ left, top });
    setSelectedShowForStatus(show);
    setCurrentShowStatus(getShowStatus(show, userLists));
    setShowStatusMenu(true);
  };

  const handleRatingClick = (e, show) => {
    e.preventDefault();
    e.stopPropagation();
    if (!handleProtectedAction()) return;
    
    setSelectedShowForRating(show);
    setShowRatingOnlyModal(true);
  };

  const handleWatchlistClick = async (e, show) => {
    e.preventDefault();
    e.stopPropagation();
    if (!handleProtectedAction()) return;

    try {
      await addToWatchlist(user.uid, show);
      setToast({ message: 'Sparad i din lista!', type: 'success' });
    } catch (error) {
      console.error('Error adding to watchlist:', error);
      setToast({ message: 'Kunde inte spara', type: 'error' });
    }
  };

  const handleShare = async (e, show) => {
    e.preventDefault();
    if (!handleProtectedAction()) return;

    const shareData = {
      title: show.title,
      text: `Kolla in ${show.title} på Smultron!`,
      url: `${window.location.origin}/show/${show.id}`
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(shareData.url);
        setToast({ message: 'Länk kopierad!', type: 'success' });
      }

      // Add activity tracking for share with a value
      await addActivity(
        user.uid,
        'share',
        show.id,
        show.title,
        'shared'
      );
    } catch (error) {
      console.error('Error sharing:', error);
      if (error.name !== 'AbortError') {
        setToast({ message: 'Kunde inte dela', type: 'error' });
      }
    }
  };

  const handleRatingOverviewClick = async (e, show) => {
    e.preventDefault();
    setSelectedShowForRating(show);
    setShowRatingsOverview(true);
    setLoadingRatings(true);

    try {
      const ratingsData = await getShowRatings(show.id);
      const ratingsWithUserDetails = await Promise.all(
        ratingsData.ratings.map(async (rating) => {
          const userDoc = await getDoc(doc(db, 'users', rating.userId));
          const userData = userDoc.data();
          let userName = 'Anonymous';
          if (userData?.displayName) {
            userName = userData.displayName;
          } else if (userData?.firstName || userData?.lastName) {
            userName = `${userData.firstName || ''} ${userData.lastName || ''}`.trim();
          }
          return {
            ...rating,
            userName,
            userPhoto: userData?.photoURL
          };
        })
      );
      setShowRatings(ratingsWithUserDetails);
    } catch (error) {
      console.error('Error fetching ratings:', error);
      setToast({ message: 'Kunde inte hämta betyg', type: 'error' });
    } finally {
      setLoadingRatings(false);
    }
  };

  const handleMarkAsSeen = async (e, show) => {
    e.preventDefault();
    e.stopPropagation();
    if (!handleProtectedAction()) return;

    try {
      await markShowAsSeen(user.uid, show);
      setToast({ message: 'Markerad som sedd!', type: 'success' });
    } catch (error) {
      console.error('Error marking as seen:', error);
      setToast({ message: 'Kunde inte markera som sedd', type: 'error' });
    }
  };

  const getShowStatus = (show, lists) => {
    if (!lists) return 'none';
    
    const isInWatching = lists.watchingList?.some(item => String(item.id) === String(show.id));
    const isInWatchlist = lists.watchlist?.some(item => String(item.id) === String(show.id));
    const isInSeen = lists.seenList?.some(item => String(item.id) === String(show.id));
    const isInArchived = lists.archivedList?.some(item => String(item.id) === String(show.id));
    
    if (isInWatching) return SHOW_STATUSES.WATCHING;
    if (isInWatchlist) return SHOW_STATUSES.WATCHLIST;
    if (isInSeen) return SHOW_STATUSES.SEEN;
    if (isInArchived) return SHOW_STATUSES.ARCHIVED;
    return SHOW_STATUSES.NONE;
  };

  const getStatusMessage = (status) => {
    switch (status) {
      case SHOW_STATUSES.WATCHING:
        return 'Tillagd i Tittar på!';
      case SHOW_STATUSES.WATCHLIST:
        return 'Tillagd i Ska se!';
      case SHOW_STATUSES.SEEN:
        return 'Markerad som sedd!';
      case SHOW_STATUSES.ARCHIVED:
        return 'Arkiverad!';
      default:
        return 'Status uppdaterad!';
    }
  };

  const handleStatusChange = async (show, newStatus) => {
    if (!handleProtectedAction()) return;

    try {
      console.log('Starting status change for:', show.title, 'to:', newStatus);
      
      // Create updated lists object
      const updatedLists = { ...userLists };
      
      // Remove show from all lists first
      ['watchingList', 'watchlist', 'seenList', 'archivedList'].forEach(listName => {
        if (updatedLists[listName]) {
          updatedLists[listName] = updatedLists[listName].filter(
            item => String(item.id) !== String(show.id)
          );
        }
      });

      // Add show to appropriate list
      switch (newStatus) {
        case SHOW_STATUSES.WATCHLIST:
          if (!updatedLists.watchlist) updatedLists.watchlist = [];
          updatedLists.watchlist.push(show);
          break;
        case SHOW_STATUSES.WATCHING:
          if (!updatedLists.watchingList) updatedLists.watchingList = [];
          updatedLists.watchingList.push(show);
          break;
        case SHOW_STATUSES.SEEN:
          if (!updatedLists.seenList) updatedLists.seenList = [];
          updatedLists.seenList.push(show);
          break;
        case SHOW_STATUSES.ARCHIVED:
          if (!updatedLists.archivedList) updatedLists.archivedList = [];
          updatedLists.archivedList.push(show);
          break;
      }

      // Update UI immediately
      setUserLists(updatedLists);

      // Update the show in the appropriate lists
      const updateShowInLists = (showToUpdate) => {
        // Update rated shows if they exist
        setRatedShows(prev => prev.map(s => 
          String(s.id) === String(showToUpdate.id) ? { ...s, status: newStatus } : s
        ));

        // Update the specific list based on new status
        switch (newStatus) {
          case SHOW_STATUSES.WATCHING:
            setWatchingShows(prev => [...prev, showToUpdate]);
            break;
          case SHOW_STATUSES.WATCHLIST:
            setWatchlistShows(prev => [...prev, showToUpdate]);
            break;
          case SHOW_STATUSES.SEEN:
            setSeenShows(prev => [...prev, showToUpdate]);
            break;
        }

        // Remove from other lists
        if (newStatus !== SHOW_STATUSES.WATCHING) {
          setWatchingShows(prev => prev.filter(s => String(s.id) !== String(showToUpdate.id)));
        }
        if (newStatus !== SHOW_STATUSES.WATCHLIST) {
          setWatchlistShows(prev => prev.filter(s => String(s.id) !== String(showToUpdate.id)));
        }
        if (newStatus !== SHOW_STATUSES.SEEN) {
          setSeenShows(prev => prev.filter(s => String(s.id) !== String(showToUpdate.id)));
        }
      };

      // Update all lists immediately
      updateShowInLists(show);
      
      setShowStatusMenu(false);
      setSelectedShowForStatus(null);
      setToast({ message: getStatusMessage(newStatus), type: 'success' });

      // Perform database updates in the background
      try {
        switch (newStatus) {
          case SHOW_STATUSES.WATCHLIST:
            await addToWatchlist(user.uid, show);
            await addActivity(user.uid, 'status', show.id, show.title, 'watchlist');
            break;
          case SHOW_STATUSES.WATCHING:
            await markShowAsWatching(user.uid, show);
            await addActivity(user.uid, 'status', show.id, show.title, 'watching');
            break;
          case SHOW_STATUSES.SEEN:
            await markShowAsSeen(user.uid, show);
            await addActivity(user.uid, 'status', show.id, show.title, 'seen');
            break;
          case SHOW_STATUSES.ARCHIVED:
            await archiveShow(user.uid, show);
            await addActivity(user.uid, 'status', show.id, show.title, 'archived');
            break;
        }
      } catch (error) {
        // If database update fails, revert the UI changes
        console.error('Error updating database:', error);
        setUserLists(prevLists => ({...prevLists}));
        updateShowInLists({ ...show, status: currentShowStatus });
        setToast({ message: 'Kunde inte ändra status', type: 'error' });
      }
    } catch (error) {
      console.error('Error in handleStatusChange:', error);
      setToast({ message: 'Kunde inte ändra status', type: 'error' });
    }
  };

  // Add this effect to fetch user lists
  useEffect(() => {
    const fetchUserLists = async () => {
      if (!user) return;
      try {
        const lists = await getUserLists(user.uid);
        setUserLists(lists);
      } catch (error) {
        console.error('Error fetching user lists:', error);
      }
    };

    fetchUserLists();
  }, [user]);

  const handleRatingOnlySubmit = async (rating) => {
    if (!user || !selectedShowForRating) return;

    // Store the previous state for rollback if needed
    const previousRatedShows = [...ratedShows];
    const previousWatchingShows = [...watchingShows];
    const previousWatchlistShows = [...watchlistShows];
    const previousSeenShows = [...seenShows];
    const previousShowRatings = [...showRatings];

    try {
      // Submit the rating to the backend first to get the new averages
      const { averageRating, ratingCount } = await addRating(
        user.uid, 
        selectedShowForRating.id, 
        rating
      );

      // Create updated show object with new rating data
      const updatedShow = {
        ...selectedShowForRating,
        rating,  // User's personal rating
        userRating: rating,
        averageRating, // Use the new average from backend
        ratingCount,   // Use the new count from backend
        ratedAt: new Date()
      };

      // Update all lists with the new rating data
      const updateListWithNewRating = (setList, currentList) => {
        setList(currentList.map(show =>
          show.id === selectedShowForRating.id 
            ? { 
                ...show, 
                rating,
                userRating: rating,
                averageRating,
                ratingCount
              }
            : show
        ));
      };

      // Add to rated shows if not exists
      setRatedShows(prev => {
        const exists = prev.some(show => show.id === selectedShowForRating.id);
        if (!exists) {
          return [updatedShow, ...prev];
        }
        return prev.map(show => 
          show.id === selectedShowForRating.id ? updatedShow : show
        );
      });

      // Update all other lists
      updateListWithNewRating(setWatchingShows, watchingShows);
      updateListWithNewRating(setWatchlistShows, watchlistShows);
      updateListWithNewRating(setSeenShows, seenShows);

      // Update ratings overview if open
      if (showRatingsOverview) {
        const newRating = {
          userId: user.uid,
          rating,
          timestamp: new Date(),
          userName: user.displayName || 'You',
          userPhoto: user.photoURL
        };

        setShowRatings(prev => {
          const exists = prev.some(r => r.userId === user.uid);
          if (exists) {
            return prev.map(r => r.userId === user.uid ? newRating : r);
          }
          return [newRating, ...prev];
        });
      }

      setShowRatingOnlyModal(false);
      setToast({ message: 'Betyg sparat!', type: 'success' });

      // Add activity
      await addActivity(
        user.uid,
        'rating',
        selectedShowForRating.id,
        selectedShowForRating.title,
        rating
      );

    } catch (error) {
      console.error('Error submitting rating:', error);
      // Rollback on error
      setRatedShows(previousRatedShows);
      setWatchingShows(previousWatchingShows);
      setWatchlistShows(previousWatchlistShows);
      setSeenShows(previousSeenShows);
      setShowRatings(previousShowRatings);
      setToast({ message: 'Kunde inte spara betyg', type: 'error' });
    }
  };

  // Add this handler for filter changes
  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    pageStateStore.setState('friendProfile', { 
      ...pageStateStore.getState('friendProfile'),
      filter: newFilter 
    });
  };

  const handleAddStreamingSource = async (source) => {
    if (!handleProtectedAction()) return;
    
    try {
      console.log('Adding streaming source:', source, 'for:', selectedShowForSources?.title);
      
      // Show success toast immediately
      setToast({ message: 'Streamingtjänst tillagd!', type: 'success' });

      // Update local state immediately
      setSelectedShowForSources(prev => ({
        ...prev,
        streamingSources: [...(prev.streamingSources || []), source]
      }));

      // Update the show in the appropriate list based on activeTab
      const updateShowInList = (shows) => 
        shows.map(show => 
          show.id === selectedShowForSources.id 
            ? {
                ...show,
                streamingSources: [...(show.streamingSources || []), source]
              }
            : show
        );

      switch (activeTab) {
        case 'rated':
          setRatedShows(updateShowInList(ratedShows));
          break;
        case 'watching':
          setWatchingShows(updateShowInList(watchingShows));
          break;
        case 'watchlist':
          setWatchlistShows(updateShowInList(watchlistShows));
          break;
        case 'seen':
          setSeenShows(updateShowInList(seenShows));
          break;
      }

      // Update Firestore in the background
      const showRef = doc(db, 'shows', selectedShowForSources.id);
      const showDoc = await getDoc(showRef);
      
      if (showDoc.exists()) {
        const currentSources = showDoc.data().streamingSources || [];
        if (!currentSources.includes(source)) {
          await updateDoc(showRef, {
            streamingSources: arrayUnion(source)
          });
        }
      }

      await addActivity(
        user.uid,
        'streaming',
        selectedShowForSources.id,
        selectedShowForSources.title,
        source
      );
      console.log('Streaming activity added successfully');
    } catch (error) {
      console.error('Error adding streaming source:', error);
      // Show error toast and revert changes if the update failed
      setToast({ message: 'Kunde inte lägga till streamingtjänst', type: 'error' });
      // Revert local state changes
      setSelectedShowForSources(prev => ({
        ...prev,
        streamingSources: prev.streamingSources.filter(s => s !== source)
      }));
      // Revert list changes
      // ... similar switch statement to revert changes
    }
  };

  const handleRemoveStreamingSource = async (source) => {
    if (!handleProtectedAction()) return;
    
    try {
      // Show success toast immediately
      setToast({ message: 'Streamingtjänst borttagen!', type: 'success' });

      // Update local state immediately
      setSelectedShowForSources(prev => ({
        ...prev,
        streamingSources: prev.streamingSources.filter(s => s !== source)
      }));

      // Update the show in the appropriate list based on activeTab
      const updateShowInList = (shows) => 
        shows.map(show => 
          show.id === selectedShowForSources.id 
            ? {
                ...show,
                streamingSources: show.streamingSources.filter(s => s !== source)
              }
            : show
        );

      switch (activeTab) {
        case 'rated':
          setRatedShows(updateShowInList(ratedShows));
          break;
        case 'watching':
          setWatchingShows(updateShowInList(watchingShows));
          break;
        case 'watchlist':
          setWatchlistShows(updateShowInList(watchlistShows));
          break;
        case 'seen':
          setSeenShows(updateShowInList(seenShows));
          break;
      }

      // Update Firestore in the background
      const showRef = doc(db, 'shows', selectedShowForSources.id);
      await updateDoc(showRef, {
        streamingSources: arrayRemove(source)
      });

      // Add activity tracking for removal
      await addActivity(
        user.uid,
        'streaming_remove',
        selectedShowForSources.id,
        selectedShowForSources.title,
        source
      );
    } catch (error) {
      console.error('Error removing streaming source:', error);
      setToast({ message: 'Kunde inte ta bort streamingtjänst', type: 'error' });
      // Revert local state changes
      setSelectedShowForSources(prev => ({
        ...prev,
        streamingSources: [...prev.streamingSources, source]
      }));
    }
  };

  // Add to the initial data fetching
  useEffect(() => {
    if (user?.uid && userId && user.uid !== userId) {
      updateLastViewedTimestamp(user.uid, userId);
    }
  }, [user?.uid, userId]);

  if (loading && !friendProfile) {
    return <FriendProfileSkeleton />;
  }
  
  if (!friendProfile) {
    return <div className="friend-profile-container">User not found</div>;
  }
  
  return (
    <div className="friend-profile-container">
      <div className="friend-header">
        <UserAvatar user={friendProfile} size="large" />
        <div className="friend-info">
          <h3>{isOwnProfile ? 'Mina smultron' : `${friendProfile?.displayName}'s Smultron`}</h3>
          {lastActive && (
            <span className="last-active">
              Senast aktiv: {new Date(lastActive).toLocaleDateString('sv-SE')}
            </span>
          )}
        </div>
      </div>

      <div className="tab-container">
        <button 
          className={`tab-button ${activeTab === 'activity' ? 'active' : ''}`}
          onClick={() => handleTabChange('activity')}
        >
          Aktivitet
        </button>
        <button 
          className={`tab-button ${activeTab === 'rated' ? 'active' : ''}`}
          onClick={() => handleTabChange('rated')}
        >
          Topplista
        </button>
        <button 
          className={`tab-button ${activeTab === 'watching' ? 'active' : ''}`}
          onClick={() => handleTabChange('watching')}
        >
          Tittar på
        </button>
        <button 
          className={`tab-button ${activeTab === 'watchlist' ? 'active' : ''}`}
          onClick={() => handleTabChange('watchlist')}
        >
          Ska se
        </button>
        <button 
          className={`tab-button ${activeTab === 'seen' ? 'active' : ''}`}
          onClick={() => handleTabChange('seen')}
        >
          Har sett
        </button>
      </div>

      {/* Only show filter if there are shows */}
      {activeTab !== 'activity' && hasShowsForCurrentTab(activeTab, ratedShows, watchingShows, watchlistShows, seenShows) && (
        <CustomSelect
          value={filter}
          onChange={handleFilterChange}
        />
      )}

      <div className={`${activeTab === 'activity' ? 'activity-content' : 'shows-list'}`}>
        {activeTab === 'activity' ? (
          loadingActivities ? (
            <div className="activity-loading">Laddar aktiviteter...</div>
          ) : activities.length > 0 ? (
            activities.map((activity) => (
              <div key={activity.id} className="activity-item">
                <div className="activity-content">
                  <span className="activity-text">
                    {formatActivityText(activity, friendProfile)}
                  </span>
                  <span className="activity-time">
                    {formatTimestamp(activity.timestamp)}
                  </span>
                </div>
              </div>
            ))
          ) : (
            <div className="empty-state">
              <p>Ingen aktivitet än</p>
            </div>
          )
        ) : (
          renderShows()
        )}
      </div>

      {showStreamingModal && (
        <StreamingSourceModal
          show={selectedShowForSources}
          onClose={() => setShowStreamingModal(false)}
          existingSources={selectedShowForSources?.streamingSources || []}
          onAddSource={handleAddStreamingSource}
          onRemoveSource={handleRemoveStreamingSource}
        />
      )}

      {showStatusMenu && selectedShowForStatus && (
        <StatusMenu
          show={selectedShowForStatus}
          position={menuPosition}
          onClose={() => {
            setShowStatusMenu(false);
            setSelectedShowForStatus(null);
          }}
          currentStatus={currentShowStatus}
          onStatusChange={handleStatusChange}
        />
      )}

      {showRatingOnlyModal && selectedShowForRating && (
        <RatingOnlyModal
          show={selectedShowForRating}
          onClose={() => setShowRatingOnlyModal(false)}
          onSubmit={(rating) => handleRatingOnlySubmit(rating)}
        />
      )}

      {showRatingsOverview && (
        <RatingsOverviewModal
          show={selectedShowForRating}
          ratings={showRatings}
          isLoading={loadingRatings}
          onClose={() => {
            setShowRatingsOverview(false);
            setShowRatings([]);
          }}
        />
      )}

      {showLoginPrompt && (
        <LoginPrompt 
          onClose={() => setShowLoginPrompt(false)}
          message="Du måste vara inloggad för att göra detta."
        />
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}

// Add these helper functions (similar to Activity.jsx but simplified for single user)
const formatTimestamp = (timestamp) => {
  if (!timestamp) return '';
  
  const date = timestamp.toDate();
  const now = new Date();
  const diffInHours = (now - date) / (1000 * 60 * 60);
  
  if (diffInHours < 24) {
    if (diffInHours < 1) {
      const minutes = Math.floor((now - date) / (1000 * 60));
      return `${minutes} min sedan`;
    }
    return `${Math.floor(diffInHours)} tim sedan`;
  }
  
  return date.toLocaleDateString('sv-SE', {
    year: date.getFullYear() === now.getFullYear() ? undefined : 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export default FriendProfile; 